/**
 * Allowed and mandatory columns per lookup type, mirroring the backend `ImportSchema`
 * definitions in outsmart-metadata-lookup (data_import/models.py). The transformer
 * config maps source-file columns onto these target column names.
 */

import { CONCRETE_LOOKUP_TYPES } from './constants.ts';

export type LookupType = (typeof CONCRETE_LOOKUP_TYPES)[number];

export type TransformerSchema = {
    allowedColumns: readonly string[];
    mandatoryColumns: readonly string[];
};

const ARTICLE_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'article_id1',
        'article_id2',
        'article_id3',
        'description',
        'description_boosted',
        'gross_price',
        'order_units',
        'sales_units',
        'variants',
        'order_unit_conversion_target',
        'order_unit_conversion_factor',
        'order_unit_conversion_source',
        'sales_organisation',
        'article_status',
        'extra_data',
    ],
    mandatoryColumns: ['article_id1', 'description'],
};

const CLIENT_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'client_id1',
        'client_id2',
        'client_id3',
        'client_type',
        'client_contact_person1',
        'company_name',
        'misc',
        'address1',
        'address2',
        'street_name',
        'street_number',
        'zip_code',
        'city',
        'po_box',
        'country_subdivision',
        'country',
        'domain',
        'iln',
        'vat',
        'locale_numeric',
        'locale',
        'internal_note',
        'iban',
        'swift',
        'extra_data',
    ],
    mandatoryColumns: ['client_id1'],
};

const CONTACT_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'contact_id',
        'first_name',
        'last_name',
        'email',
        'phone',
        'mobile_phone',
        'extra_data',
    ],
    mandatoryColumns: ['partition_id', 'contact_id', 'first_name', 'last_name'],
};

const ADDRESS_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'address_id1',
        'address_id2',
        'address_id3',
        'name',
        'misc',
        'contact_person',
        'address1',
        'address2',
        'street_name',
        'street_number',
        'zip_code',
        'city',
        'po_box',
        'country_subdivision',
        'country',
        'phone',
        'extra_data',
    ],
    mandatoryColumns: ['address_id1'],
};

const CONVERSION_FACTOR_SCHEMA: TransformerSchema = {
    allowedColumns: ['partition_id', 'article_id', 'source_unit', 'target_unit', 'conversion_quotient', 'extra_data'],
    mandatoryColumns: ['source_unit', 'target_unit', 'conversion_quotient'],
};

const FRAMEWORK_CONTRACT_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'client_id',
        'client_name',
        'contract_id1',
        'contract_id2',
        'contract_name',
        'extra_data',
    ],
    mandatoryColumns: ['partition_id', 'contract_id1'],
};

const ORDER_MATCHING_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'order_number',
        'external_reference',
        'order_date',
        'order_total_price',
        'delivery_address',
        'receiver_number',
        'position_number',
        'article_id1',
        'article_id2',
        'article_id3',
        'article_description',
        'article_price',
        'quantity',
        'total_price',
        'delivery_date',
        'unit',
        'payment_terms_discount',
        'payment_terms_due_days',
        'payment_terms_type',
        'iban',
        'swift',
        'account',
        'extra_data',
    ],
    mandatoryColumns: ['order_number'],
};

const OFFER_MATCHING_SCHEMA: TransformerSchema = {
    allowedColumns: [
        'partition_id',
        'offer_number',
        'valid_from',
        'valid_until',
        'position_number',
        'article_id1',
        'article_id2',
        'article_id3',
        'article_description',
        'quantity',
        'unit',
        'extra_data',
    ],
    mandatoryColumns: ['offer_number'],
};

const SCHEMAS: Record<LookupType, TransformerSchema> = {
    ARTICLE_LOOKUP: ARTICLE_SCHEMA,
    CLIENT_LOOKUP: CLIENT_SCHEMA,
    CONTACT_LOOKUP: CONTACT_SCHEMA,
    ADDRESS_LOOKUP: ADDRESS_SCHEMA,
    CONVERSION_FACTOR_LOOKUP: CONVERSION_FACTOR_SCHEMA,
    FRAMEWORK_CONTRACT_LOOKUP: FRAMEWORK_CONTRACT_SCHEMA,
    ORDER_MATCHING_LOOKUP: ORDER_MATCHING_SCHEMA,
    OFFER_MATCHING_LOOKUP: OFFER_MATCHING_SCHEMA,
};

export const getTransformerSchema = (lookupType: string | null | undefined): TransformerSchema | null => {
    if (!lookupType) return null;
    return SCHEMAS[lookupType as LookupType] ?? null;
};
